import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../assets/css/ContactModal.css";

function ContactModal() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const email = "turasov1606@gmail.com";
  const phone = "+38-050-029-76-77";

  // Listen for global trigger to open modal from other buttons
  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
    };

    window.addEventListener("open-contact-modal", handleOpen);
    return () => window.removeEventListener("open-contact-modal", handleOpen);
  }, []);

  // Handle ESC key and scroll lock
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError(t("contacts.form_error_empty") || "Please fill in all fields.");
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey || 
        serviceId === "your_emailjs_service_id" || 
        templateId === "your_emailjs_template_id" || 
        publicKey === "your_emailjs_public_key") {
      setError(t("contacts.form_error_config") || "Email service is not configured. Please fill in the keys in .env file.");
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      service_id: serviceId,
      template_id: templateId,
      user_id: publicKey,
      template_params: {
        from_name: formData.name,
        from_email: formData.email,
        name: formData.name,
        email: formData.email,
        message: formData.message,
        to_name: "Danilo"
      }
    };

    try {
      const response = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const textResponse = await response.text();
        setError(`${t("contacts.form_error_failed") || "Sending failed:"} ${textResponse}`);
      }
    } catch (err) {
      setError(t("contacts.form_error_network") || "Network error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setError("");
    }, 300);
  };

  return (
    <>
      {/* Fixed Floating Contact Button (FAB) */}
      <button 
        className="floating-contact-btn"
        onClick={() => setIsOpen(true)}
        aria-label="Open contact form"
        title={t("contacts.open_chat", "Quick Message")}
      >
        <span className="floating-contact-status-dot"></span>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span className="floating-contact-tooltip">
          {t("contacts.open_chat", "Send Message")}
        </span>
      </button>

      {/* Modal Dialog */}
      {isOpen && (
        <div className="contact-modal-backdrop" onClick={handleClose}>
          <div 
            className="contact-modal-dialog" 
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Top Mockup Bar */}
            <div className="contact-modal-top-bar">
              <div className="contact-modal-dots">
                <span className="contact-modal-dot dot-red"></span>
                <span className="contact-modal-dot dot-yellow"></span>
                <span className="contact-modal-dot dot-green"></span>
              </div>
              <span className="contact-modal-window-title">danilo.sendMessage()</span>
              {/* Close Button */}
              <button 
                className="contact-modal-close-btn"
                onClick={handleClose}
                aria-label="Close modal"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="contact-modal-content">
              {!submitted ? (
                <>
                  <div className="contact-modal-header">
                    <div className="contact-modal-title-row">
                      <h3 className="contact-modal-title gradient-text">
                        {t("contacts.form_title", "Send Me a Message")}
                      </h3>
                      <div className="status-badge" style={{ margin: 0, padding: "3px 10px", fontSize: "10px" }}>
                        <span className="status-dot"></span>
                        <span>Online</span>
                      </div>
                    </div>
                    <p className="contact-modal-subtitle">
                      {t("contacts.modal_subtitle", "Have a job proposal or want to discuss a project? Fill out the form below and I'll reply promptly.")}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="contact-modal-form">
                    {/* Name Field */}
                    <div className="contact-modal-form-group">
                      <label htmlFor="modal-name">{t("contacts.form_name", "Full Name")}</label>
                      <div className="contact-input-icon-wrapper">
                        <span className="contact-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </span>
                        <input
                          type="text"
                          id="modal-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="contact-modal-input"
                          placeholder="John Doe"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="contact-modal-form-group">
                      <label htmlFor="modal-email">{t("contacts.form_email", "Email Address")}</label>
                      <div className="contact-input-icon-wrapper">
                        <span className="contact-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                            <polyline points="22,6 12,13 2,6"></polyline>
                          </svg>
                        </span>
                        <input
                          type="email"
                          id="modal-email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="contact-modal-input"
                          placeholder="john@example.com"
                          disabled={loading}
                          required
                        />
                      </div>
                    </div>

                    {/* Message Field */}
                    <div className="contact-modal-form-group">
                      <label htmlFor="modal-message">{t("contacts.form_message", "Message")}</label>
                      <div className="contact-input-icon-wrapper textarea-icon-wrapper">
                        <span className="contact-input-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          </svg>
                        </span>
                        <textarea
                          id="modal-message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className="contact-modal-textarea"
                          placeholder={t("contacts.form_message_placeholder", "Your message details...")}
                          disabled={loading}
                          rows="4"
                          required
                        />
                      </div>
                    </div>

                    {error && (
                      <p style={{ color: "var(--accent-4, #dc2626)", fontSize: "13px", margin: "0", lineHeight: "1.4" }}>
                        {error}
                      </p>
                    )}

                    <button 
                      type="submit" 
                      className="btn btn-primary contact-modal-submit-btn"
                      disabled={loading}
                    >
                      {loading ? (
                        <span>{t("contacts.form_sending", "Sending...")}</span>
                      ) : (
                        <>
                          <span>{t("contacts.form_submit", "Submit Message")}</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                          </svg>
                        </>
                      )}
                    </button>
                  </form>

                  {/* Direct Contact Links */}
                  <div className="contact-modal-direct-footer">
                    <a href={`mailto:${email}`} className="contact-modal-direct-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <span>{email}</span>
                    </a>
                    <a href={`tel:${phone.replace(/[^+0-9]/g, "")}`} className="contact-modal-direct-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                      <span>{phone}</span>
                    </a>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "20px 10px" }}>
                  <div style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "rgba(16, 185, 129, 0.12)",
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    margin: "0 auto 16px"
                  }}>
                    ✓
                  </div>
                  <h3 className="gradient-text" style={{ fontSize: "1.5rem", marginBottom: "8px" }}>
                    {t("contacts.form_success_title", "Success!")}
                  </h3>
                  <p style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: "1.6", maxWidth: "340px", margin: "0 auto 24px" }}>
                    {t("contacts.form_success_text", "Your message was sent successfully. I'll get back to you shortly!")}
                  </p>
                  <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: "", email: "", message: "" });
                      }}
                    >
                      {t("contacts.form_send_again", "Send Another")}
                    </button>
                    <button 
                      className="btn btn-primary" 
                      onClick={handleClose}
                    >
                      OK
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default ContactModal;
